/**
 * Read-only lore file system for the TERMINAL app (ADR-0025 item 3).
 *
 *   /var/log/            asset dossiers, from the content pack `lore.assets`
 *   ~/expediente/        Hronir fragments, from the player's own fragment list
 *   ~/.prev_operator     hidden; revealed by the Konami code (ADR-0025 item 4)
 *
 * No lore is invented here. Anything the content team still owes is a short
 * header marked `[PLACEHOLDER // CONTENT TEAM]`. Fragment text is only ever
 * read through `fragmentDisplayText`, so REDACTED stays REDACTED.
 */
import { fragmentDisplayText, fragmentSource } from '../fragmentModel';

export const HOME = '/home/operator';

export const PLACEHOLDER_MARK = '[PLACEHOLDER // CONTENT TEAM]';

export const VAR_LOG_README = [
  `${PLACEHOLDER_MARK} replace with the real log index copy.`,
  'One dossier per asset the street trades. Read with: cat /var/log/<name>.log',
];

export const EXPEDIENTE_README = [
  `${PLACEHOLDER_MARK} replace with the real expediente intro copy.`,
  'Fragments the Archive has released to you. Redacted ones stay redacted',
  'until you decrypt them in the EXPEDIENTE app.',
];

/** Short placeholder trace of the previous invoked user. Content team to replace. */
export const PREV_OPERATOR_TRACE = [
  `${PLACEHOLDER_MARK} previous invoked user trace. Replace this file.`,
  '',
  '~/.prev_operator  CHECKSUM FAIL (3 of 7 blocks unreadable)',
  'LAST SEEN ........ ??:??:?? [CORRUPT]',
  'OPERATOR ......... ########  (name field overwritten)',
  'SESSION .......... 0x00 0x00 0x00 0x?? 0x00',
  '> buy w#ed 1',
  '> the line did not cl%se',
  '> who is typing now',
  'END OF READABLE DATA',
];

const MISSING = (what) => [{ t: `${what}: NO SUCH FILE OR DIRECTORY`, k: 'err' }];

function normalize(path) {
  let p = String(path || '').trim();
  if (!p || p === '~') p = HOME;
  else if (p.startsWith('~/')) p = HOME + p.slice(1);
  const parts = [];
  for (const seg of p.split('/')) {
    if (!seg || seg === '.') continue;
    if (seg === '..') parts.pop();
    else parts.push(seg);
  }
  return `/${parts.join('/')}`;
}

function assetLog(id, entry) {
  return [
    `ASSET   ${entry.streetName || id}`,
    entry.orbisAlias ? `ALIAS   ${entry.orbisAlias}` : null,
    entry.origin ? `ORIGIN  ${entry.origin}` : null,
    entry.function ? `FUNCTION ${entry.function}` : null,
  ].filter(Boolean);
}

/**
 * @param deps.getContent    () => Promise<contentPack|null>
 * @param deps.getFragments  () => Promise<fragment[]>   (server list: text already redacted)
 * @param deps.prevRevealed  () => boolean
 */
export function createFs(deps) {
  async function build() {
    const nodes = new Map();
    const dir = (path, hidden = false) => nodes.set(path, { type: 'dir', children: [], hidden });
    const add = (path, node) => {
      nodes.set(path, { type: 'file', ...node });
      const parent = path.slice(0, path.lastIndexOf('/')) || '/';
      nodes.get(parent).children.push(path);
    };
    const addDir = (path) => {
      dir(path);
      const parent = path.slice(0, path.lastIndexOf('/')) || '/';
      if (nodes.has(parent)) nodes.get(parent).children.push(path);
    };

    dir('/');
    addDir('/var');
    addDir('/var/log');
    addDir('/home');
    addDir(HOME);
    addDir(`${HOME}/expediente`);

    add('/var/log/README.log', { lines: VAR_LOG_README });
    const content = await deps.getContent().catch(() => null);
    for (const [id, entry] of Object.entries(content?.lore?.assets || {})) {
      add(`/var/log/${id}.log`, { lines: assetLog(id, entry) });
    }

    add(`${HOME}/expediente/README.txt`, { lines: EXPEDIENTE_README });
    const fragments = await deps.getFragments().catch(() => []);
    for (const f of fragments || []) {
      const decrypted = f.visibility === 'DECRYPTED';
      add(`${HOME}/expediente/${f.fragmentId}.txt`, {
        tag: decrypted ? '' : '[REDACTED]',
        lines: [
          `TITLE   ${f.title || 'UNTITLED'}`,
          `SOURCE  ${fragmentSource(f)}`,
          `STATE   ${f.visibility}`,
          '',
          fragmentDisplayText(f),
        ],
      });
    }

    if (deps.prevRevealed()) {
      add(`${HOME}/.prev_operator`, { hidden: true, glitch: true, lines: PREV_OPERATOR_TRACE });
    }
    return nodes;
  }

  return {
    async ls(args = []) {
      const showAll = args.some((a) => a === '-a' || a === '-la' || a === '-al');
      const target = args.find((a) => !a.startsWith('-'));
      const path = normalize(target);
      const nodes = await build();
      const node = nodes.get(path);
      if (!node) return MISSING(target || path);
      if (node.type === 'file') return [{ t: path, k: 'out' }];
      const rows = node.children
        .map((p) => ({ p, n: nodes.get(p) }))
        .filter(({ p, n }) => showAll || !(n.hidden || p.split('/').pop().startsWith('.')))
        .sort((a, b) => a.p.localeCompare(b.p));
      if (!rows.length) return [{ t: '(empty)', k: 'dim' }];
      return rows.map(({ p, n }) => ({
        t: `${p.split('/').pop()}${n.type === 'dir' ? '/' : ''}${n.tag ? `  ${n.tag}` : ''}`,
        k: n.tag ? 'dim' : 'out',
      }));
    },

    async cat(args = []) {
      const target = args.find((a) => !a.startsWith('-'));
      if (!target) return [{ t: 'USAGE: cat <file>', k: 'err' }];
      const path = normalize(target);
      const node = (await build()).get(path);
      if (!node) return MISSING(target);
      if (node.type === 'dir') return [{ t: `${target}: IS A DIRECTORY`, k: 'err' }];
      return node.lines.map((t) => ({ t, k: node.glitch ? 'glitch' : 'out' }));
    },
  };
}
